<template>
  <view-wrapper
    prefix="Edit"
    name="User"
    path="users"
    :loading="loading"
    :loadingText="loadingText"
  >
    <wrapper class="mt-5" :focus="true">
      <grafokett-input
        disabled
        :value="user.email"
        type="email"
        labelPlaceholder="Users email"
      />

      <p v-if="availableRoles.length > 0">Roles</p>
      <div
        class="pt-1 flex"
        v-for="(role, i) in availableRoles"
        :key="'role' + i"
      >
        <checkbox
          class="m-1"
          :value="roles.includes(role.id)"
          @input="addRemoveRole(i, role.id)"
        />
        <div
          class="cursor-pointer select-none"
          @click="addRemoveRole(i, role.id)"
        >
          <span class="block pl-5">{{ role.name }}</span>
          <span class="block pl-5 text-xs">{{ role.description }}</span>
        </div>
      </div>

      <p v-if="availablePermissions.length > 0" class="mt-5">Permissions</p>
      <div
        class="pt-2 flex"
        v-for="(permission, i) in availablePermissions"
        :key="'permission' + i"
      >
        <checkbox
          class="m-1"
          :value="permissions.includes(permission.userPermission)"
          @input="addRemovePermission(i, permission.userPermission)"
        />
        <div
          class="cursor-pointer select-none"
          @click="addRemovePermission(i, permission.userPermission)"
        >
          <span class="block pl-5">{{ permission.userPermission }}</span>
          <span class="block pl-5 text-xs">{{ permission.description }}</span>
        </div>
      </div>

      <div
        v-if="
          availableQualityAssuranceRoles &&
            availableQualityAssuranceRoles.length > 0
        "
      >
        <p class="mt-5">QualityAssurance roles</p>
        <div
          class="pt-2 flex"
          v-for="(role, i) in availableQualityAssuranceRoles"
          :key="'qarole' + i"
        >
          <checkbox
            class="m-1"
            :value="qualityAssuranceRoles.includes(role.qualityAssuranceRole)"
            @input="toggleQualityAssuranceRole(i, role.qualityAssuranceRole)"
          />
          <div
            class="cursor-pointer select-none"
            @click="toggleQualityAssuranceRole(i, role.qualityAssuranceRole)"
          >
            <span class="block pl-5">{{ role.qualityAssuranceRole }}</span>
            <span class="block pl-5 text-xs">{{ role.description }}</span>
          </div>
        </div>
      </div>
    </wrapper>

    <assign-groups
      class="mt-5"
      :groups="availableGroups"
      v-model="groups"
      @groupAdded="groupAdded"
      @removeGroup="removeGroup"
    >
      <template slot="assignedParagraph">
        Assigned groups to this user
      </template>
    </assign-groups>

    <grafokett-button
      v-if="!showDisableBox"
      class="big primary my-5"
      @click="showDisableBox = !showDisableBox"
      ><span class="text-gray-500"
        >{{ user.disabled ? "Activate" : "Inactivate" }} or delete user</span
      ></grafokett-button
    >

    <wrapper class="relative my-5" v-else :focus="showDisableBox">
      <div class="flex mb-5">
        <p class="m-0 flex-1">
          {{ user.disabled ? "Activate" : "Inactivate" }} or delete user
        </p>
        <i
          class="m-1 close-symbol cursor-pointer"
          @click="showDisableBox = !showDisableBox"
        />
      </div>
      <grafokett-button
        class="big primary white"
        @click="modalInactivate = !modalInactivate"
        >{{ user.disabled ? "Activate" : "Inactivate" }} user</grafokett-button
      >
      <div v-if="!user.disabled">
        <p class="m-0 mt-4">
          By inactivating a user, they have no longer access to Cloudlabel.
        </p>
        <p class="m-0 mb-10">You can reactivate them at any time.</p>
      </div>
      <div v-else>
        <p class="m-0 mt-4">
          By activating a user, they will have their access restored to
          Cloudlabel.
        </p>
        <p class="m-0 mb-10">You can inactivate them at any time.</p>
      </div>
      <grafokett-button
        class="big primary white"
        @click="modalDelete = !modalDelete"
        >Delete user</grafokett-button
      >
      <p class="m-0 mt-4">
        By deleting a user, they are erased from Cloudlabel.
      </p>
    </wrapper>

    <modal
      v-if="userActiveStatusChanged"
      :type="'success'"
      :heading="computeActivationStatus(true, true)"
      :description="
        `User has been successfully ${computeActivationStatus(true, false)}`
      "
    >
      <template v-slot:close>
        <div />
      </template>
    </modal>

    <modal
      v-if="userIsDeleted"
      :type="'success'"
      :heading="'Deleted!'"
      :description="'User has been successfully deleted'"
    >
      <template v-slot:close>
        <div />
      </template>
    </modal>

    <modal
      v-if="modalInactivate"
      @close="modalInactivate = !modalInactivate"
      :type="'warning'"
      :heading="computeActivationStatus(false, true)"
      :descriptions="[
        `Your are about to ${computeActivationStatus(false, true)} ${
          user.email
        }.`,
        `Are you sure you want to ${computeActivationStatus(false, true)}?`,
      ]"
    >
      <template v-slot:buttons>
        <div>
          <grafokett-button
            class="big bg-white border-primary text-primary"
            @click="toggleActivateStatusOnUser"
            >Yes, {{ computeActivationStatus(false, false) }}</grafokett-button
          >
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="modalInactivate = !modalInactivate"
            >Go back</grafokett-button
          >
        </div>
      </template>
    </modal>

    <modal
      v-if="modalDelete"
      @close="modalDelete = !modalDelete"
      :type="'warning'"
      :heading="'Delete?'"
      :descriptions="[
        `Your are about to delete ${user.email}.`,
        'Are you sure you want to delete?',
      ]"
    >
      <template v-slot:buttons>
        <div>
          <grafokett-button
            class="big bg-white border-primary text-primary"
            @click="deleteUser"
            >Yes, delete</grafokett-button
          >
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="modalDelete = !modalDelete"
            >Go back</grafokett-button
          >
        </div>
      </template>
    </modal>
  </view-wrapper>
</template>

<script>
import GrafokettButton from "../components/forms/Button.vue";
import GrafokettInput from "../components/forms/Input.vue";
import Modal from "../components/Modal";
import AssignGroups from "../components/datacomponents/AssignGroups";
import Wrapper from "../components/Wrapper";
import Checkbox from "../components/forms/Checkbox";

export default {
  components: {
    GrafokettButton,
    GrafokettInput,
    Modal,
    AssignGroups,
    Wrapper,
    Checkbox,
  },
  data() {
    return {
      showDisableBox: false,
      groups: [],
      roles: [],
      permissions: [],
      qualityAssuranceRoles: [],
      user: {},
      modalInactivate: false,
      userActiveStatusChanged: false,
      userIsDeleted: false,
      modalDelete: false,
      loading: false,
      loadingText: "User data",
    };
  },
  methods: {
    hasChangesBeenMade() {
      const changes = [
        ...this.payload.groupsToAdd,
        ...this.payload.groupsToRemove,
        ...this.payload.rolesToAdd,
        ...this.payload.rolesToRemove,
        ...this.payload.permissionsToAdd,
        ...this.payload.permissionsToRemove,
      ];
      return changes.length > 0;
    },
    async groupAdded(group) {
      this.loading = true;
      await this.$store.$api.post("/api/users/groups/assign", {
        userId: this.user.id,
        groupId: group.groupId ? group.groupId : group.id,
      });
      this.loading = false;
    },
    async removeGroup(group) {
      this.loading = true;
      await this.$store.$api.post("/api/users/groups/remove", {
        userId: this.user.id,
        groupId: group.groupId ? group.groupId : group.id,
      });
      this.loading = false;
    },
    async addRemoveRole(index, id) {
      const i = this.roles.indexOf(id);
      this.loading = true;
      if (i === -1) {
        await this.$store.$api.post("/api/users/roles/assign", {
          userId: this.user.id,
          roleId: id,
        });
        this.roles.push(id);
      } else {
        await this.$store.$api.post("/api/users/roles/remove", {
          userId: this.user.id,
          roleId: id,
        });
        this.roles.splice(i, 1);
      }
      this.loading = false;
    },
    async addRemovePermission(index, userPermission) {
      const i = this.permissions.indexOf(userPermission);
      this.loading = true;
      if (i === -1) {
        await this.$store.$api.post("/api/users/permissions/assign", {
          userId: this.user.id,
          permission: userPermission,
        });
        this.permissions.push(userPermission);
      } else {
        await this.$store.$api.post("/api/users/permissions/remove", {
          userId: this.user.id,
          permission: userPermission,
        });
        this.permissions.splice(i, 1);
      }
      this.loading = false;
    },
    async toggleQualityAssuranceRole(index, role) {
      const i = this.qualityAssuranceRoles.indexOf(role);
      this.loading = true;
      if (i === -1) {
        await this.$store.$api.post("/api/qualityassurance/roles/assign", {
          userId: this.user.id,
          role: role,
        });
        this.qualityAssuranceRoles.push(role);
      } else {
        await this.$store.$api.post("/api/qualityassurance/roles/remove", {
          userId: this.user.id,
          role: role,
        });
        this.qualityAssuranceRoles.splice(i, 1);
      }
      this.loading = false;
    },
    async getData() {
      await this.$store.dispatch("GROUPS/GET_AVAILABLE_GROUPS");
      await this.$store.dispatch("ROLES/GET");
      await this.$store.dispatch("PERMISSIONS/GET");
      this.loadQualityAssuranceRoles();
      await this.$store.dispatch("USERS/GET");
      this.user = this.$store.getters["USERS/USERS"].filter(
        e => e.id === this.$route.params["id"]
      )[0];
      this.roles = this.user.roles.map(e => e.id);
      this.permissions = [...this.user.permissions];
      this.qualityAssuranceRoles = [...this.user.qualityAssuranceRoles];
      this.groups = [...this.user.groups];
    },
    async toggleActivateStatusOnUser() {
      this.loading = true;
      try {
        if (this.user.disabled) {
          await this.$store.dispatch("USERS/ACTIVATE", this.user.id);
        } else {
          await this.$store.dispatch("USERS/INACTIVATE", this.user.id);
        }
        this.loading = false;
        this.modalInactivate = false;
        this.userActiveStatusChanged = true;
        setTimeout(() => this.$router.push({ name: "users" }), 1500);
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },
    deleteUser() {
      try {
        this.$store.dispatch("USERS/DELETE", this.user.id);
        this.$router.push({ name: "users" });
        this.modalDelete = false;
        this.userIsDeleted = true;
        setTimeout(() => this.$router.push({ name: "users" }), 5000);
      } catch (error) {
        console.error(error);
      }
    },
    computeActivationStatus(past, lowercase) {
      if (past) {
        if (lowercase) {
          return this.user.disabled ? "activated" : "inactivated";
        } else {
          return this.user.disabled ? "Activated" : "Inactivated";
        }
      } else {
        if (lowercase) {
          return this.user.disabled ? "activate" : "inactivate";
        } else {
          return this.user.disabled ? "Activate" : "Inactivate";
        }
      }
    },
    async loadQualityAssuranceRoles() {
      if (this.$store.getters["FEATURES/ENABLED_FEATURES"] === null) {
        await this.$store.dispatch("FEATURES/GET_FEATURES");
      }
      const features = this.$store.getters["FEATURES/ENABLED_FEATURES"];
      if (features.indexOf("labelVersionControlEnabled") >= 0)
        await this.$store.dispatch("QUALITY_ASSURANCE/GET_ROLES");
    },
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    this.loading = false;
  },
  computed: {
    availablePermissions() {
      return this.$store.getters["PERMISSIONS/PERMISSIONS"];
    },
    availableRoles() {
      return this.$store.getters["ROLES/ROLES"];
    },
    availableQualityAssuranceRoles() {
      return this.$store.getters["QUALITY_ASSURANCE/ROLES"];
    },
    availableGroups() {
      return this.$store.getters["GROUPS/GROUPS"];
    },
    payload() {
      const newGroups = this.groups.map(e => e.groupId);
      const preGroups = this.user.groups.map(e => e.groupId);
      const preRoles = this.user.roles.map(e => e.id);
      const prePermissions = this.user.permissions;

      const payload = {
        userId: this.user.id,
        groupsToAdd: newGroups.filter(e => !preGroups.includes(e)),
        groupsToRemove: preGroups.filter(e => !newGroups.includes(e)),
        rolesToAdd: this.roles.filter(e => !preRoles.includes(e)),
        rolesToRemove: preRoles.filter(e => !this.roles.includes(e)),
        permissionsToAdd: this.permissions.filter(
          e => !prePermissions.includes(e)
        ),
        permissionsToRemove: prePermissions.filter(
          e => !this.permissions.includes(e)
        ),
      };

      return payload;
    },
  },
};
</script>
